// External libraries
import { ApolloLink, Observable } from 'apollo-link';
import type { ErrorResponse } from 'apollo-link-error';
import { onError } from 'apollo-link-error';

// Internal libraries
import { setAccessToken } from '@/lib/globals';
import { getAccessTokenType, isAccountCreationMutation } from '../utils';
import { OPERATION_NAME_TO_RESPONSE_KEY_MAP } from '../constants';

const link = new ApolloLink((operation, forward) => {
  const { operationName } = operation;

  return new Observable(observer => {
    const subscription = forward(operation).subscribe({
      complete: observer.complete.bind(observer),
      error: observer.error.bind(observer),
      next: response => {
        const isAccountCreation = isAccountCreationMutation(operationName);

        // Set the access token in the case of a successful account creation
        if (isAccountCreation) {
          const responseObjectKey =
            // @ts-expect-error operationName doesnt need to be in the map
            OPERATION_NAME_TO_RESPONSE_KEY_MAP[operationName];
          const buyer = response?.data?.[responseObjectKey]?.buyer;

          if (buyer?.auth?.accessToken) {
            setAccessToken({
              type: getAccessTokenType(operationName),
              value: buyer.auth.accessToken,
            });
          }
        }

        observer.next(response);
      },
    });

    return () => subscription?.unsubscribe?.();
  });
});

/**
 * In the case of a post-account creation error on the SignUpNewMemberMutation
 * we need to set the access token since an account was created and the user
 * will not be able to re-submit the form. Instead we will use the access token
 * to drop a login cookie and then redirect the user to the member flow.
 */
const errorLink = onError(({ graphQLErrors, operation }: ErrorResponse) => {
  const { operationName } = operation;
  // @ts-expect-error TODO check that we are not using the empty object
  const { errorData } = graphQLErrors?.[0] || {};

  // If the error occurred after successful account creation,
  // set the access token.
  if (isAccountCreationMutation(operationName) && errorData?.accessToken) {
    setAccessToken({
      type: getAccessTokenType(operationName),
      value: errorData.accessToken,
    });
  }
});

export const authLink = link.concat(errorLink);
